import { HTMLAttributes, forwardRef } from 'react';
import './Skeleton.css';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'label' | 'metric' | 'metric-sm' | 'circle' | 'custom';
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    { width, height, variant = 'custom', className = '', style, ...props },
    ref
  ) => {
    const classNames = [
      'ui-skeleton',
      variant !== 'custom' && `ui-skeleton--${variant}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        className={classNames}
        style={{ width, height, ...style }}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export interface SkeletonCardProps extends HTMLAttributes<HTMLDivElement> {
  lines?: number;
}

export const SkeletonCard = forwardRef<HTMLDivElement, SkeletonCardProps>(
  ({ lines = 3, className = '', ...props }, ref) => {
    const classNames = ['ui-skeleton-card', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        <Skeleton variant="label" />
        <Skeleton variant="metric" />
        {Array.from({ length: lines - 2 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            style={{ width: `${70 + Math.random() * 30}%` }}
          />
        ))}
      </div>
    );
  }
);

SkeletonCard.displayName = 'SkeletonCard';

export interface SkeletonRowProps extends HTMLAttributes<HTMLDivElement> {
  columns?: number;
}

export const SkeletonRow = forwardRef<HTMLDivElement, SkeletonRowProps>(
  ({ columns = 6, className = '', ...props }, ref) => {
    const classNames = ['ui-skeleton-row', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            style={{ width: `${60 + Math.random() * 40}%` }}
          />
        ))}
      </div>
    );
  }
);

SkeletonRow.displayName = 'SkeletonRow';
